import React from 'react';
import { motion } from "motion/react";
import { Calendar, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usePageMetadata } from '../../hooks/usePageMetadata';
import apiClient from '../../api/axios';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';

import { PageHero } from '../../components/common/PageHero';

export const Veranstaltungen: React.FC = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const [page, setPage] = React.useState(1);

    // Fetch page metadata and update title
    const { data: pageMeta } = usePageMetadata('veranstaltungen');

    const { data: pagedData, isLoading } = useQuery({
        queryKey: ['publicPosters', page],
        queryFn: async () => {
            const response = await apiClient.get(`/public/posters?pageNumber=${page}&pageSize=10`);
            return response.data;
        }
    });

    const posters = pagedData?.data || [];
    const totalPages: number = pagedData?.totalPages || 1;

    return (
        <div className="flex flex-col bg-background min-h-screen">
            <PageHero
                title={pageMeta?.title || t('events.title')}
                subtitle={pageMeta?.metaDescription || t('events.description')}
                breadcrumb={[
                    { label: t('common.home'), to: '/' },
                    { label: pageMeta?.title || t('events.breadcrumb') }
                ]}
            />

            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full py-16 sm:py-24">
                {/* Events List */}
                <div key={page} className="space-y-8">
                    {isLoading ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                        </div>
                    ) : posters && posters.length > 0 ? (
                        posters.map((poster: any, index: number) => (
                            <motion.div
                                key={poster.id}
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="group relative flex flex-col items-stretch justify-between rounded-3xl bg-background border border-border shadow-sm overflow-hidden md:flex-row transition-shadow hover:shadow-lg cursor-pointer"
                                onClick={() => poster.isRedirect ? window.open(poster.pageUrl, '_blank') : navigate(`/veranstaltungen/${poster.pageUrl}`)}
                            >
                                <div className="w-full md:w-1/3 aspect-[4/3] md:aspect-auto relative overflow-hidden bg-secondary min-h-[200px]">
                                    <img
                                        src={resolveAssetUrl(poster.imageUrl) || 'https://via.placeholder.com/800x600?text=Event'}
                                        alt={poster.title}
                                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                    <div className="absolute top-4 left-4 rounded-full bg-background/90 backdrop-blur-sm px-3 py-1 text-xs font-semibold text-foreground">
                                        {t('events.event')}
                                    </div>
                                </div>

                                <div className="flex flex-1 flex-col justify-between p-8">
                                    <div>
                                        <div className="flex flex-wrap items-center gap-y-2 gap-x-6 text-sm text-foreground/60 mb-4">
                                            <div className="flex items-center gap-2">
                                                <Calendar className="h-4 w-4" />
                                                {poster.eventDate 
                                                    ? new Date(poster.eventDate).toLocaleDateString(i18n.language, { day: '2-digit', month: 'long', year: 'numeric' })
                                                    : t('events.upcoming')}
                                            </div>
                                        </div>

                                        <h3 className="text-2xl font-bold text-foreground group-hover:text-primary transition-colors mb-4">
                                            {poster.title}
                                        </h3>

                                        <p className="text-base text-foreground/80 leading-7 mb-6 line-clamp-3">
                                            {poster.description?.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()}
                                        </p>
                                    </div>

                                    {poster.redirectUrl && (
                                        <div className="flex flex-wrap items-center justify-end gap-4 border-t border-border/50 pt-6 mt-auto">
                                            <button className="inline-flex items-center gap-2 rounded-xl bg-primary/10 px-5 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground">
                                                {t('events.more_info')}
                                                <ArrowRight className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        ))
                    ) : (
                        <div className="rounded-3xl border border-dashed border-border bg-background p-16 text-center">
                            <h3 className="text-lg font-medium text-foreground">{t('events.no_events')}</h3>
                            <p className="mt-2 text-foreground/60">{t('events.check_back')}</p>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="mt-12 flex items-center justify-center gap-2">
                        <button
                            onClick={() => {
                                setPage(p => Math.max(1, p - 1));
                                window.scrollTo({ top: 300, behavior: 'smooth' });
                            }}
                            disabled={page <= 1}
                            className="p-2.5 rounded-xl border border-border bg-background text-foreground/80 hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            aria-label="Previous Page"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>

                        <div className="flex items-center gap-1.5">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    onClick={() => {
                                        setPage(p);
                                        window.scrollTo({ top: 300, behavior: 'smooth' });
                                    }}
                                    className={`w-10 h-10 rounded-xl font-medium text-sm transition-colors ${
                                        p === page
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'border border-border bg-background text-foreground/80 hover:bg-secondary'
                                    }`}
                                >
                                    {p}
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={() => {
                                setPage(p => Math.min(totalPages, p + 1));
                                window.scrollTo({ top: 300, behavior: 'smooth' });
                            }}
                            disabled={page >= totalPages}
                            className="p-2.5 rounded-xl border border-border bg-background text-foreground/80 hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            aria-label="Next Page"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                )}

            </div>
        </div>
    );
};
